import type { ReactNode } from 'react'
import {
  IconeCamadas,
  IconeEvento,
  IconeInvestigador,
  IconeOcupantes,
  IconePausar,
  IconeReiniciar,
  IconeReproduzir,
  IconeResultados,
  IconeVoltarModulo,
} from '../icones'
import type { FerramentasModulo } from './tipos'

export interface PropsCinematica {
  painelEvento: () => ReactNode
  painelOcupantes: () => ReactNode
  aoAbrirQuatroImpactos?: () => void
  painelSobreposicoes: () => ReactNode
  painelReproducao: () => ReactNode
  painelResultados: () => ReactNode
  painelMetricas?: () => ReactNode
  painelInvestigador?: () => ReactNode
  quantidadeOcupantes: number
  fase: string
  velocidadeKmh: number
  deltaVKmh?: number
  classificacaoDeltaV?: string
  reproduzindo: boolean
  aoAlternarReproducao: () => void
  aoReiniciar: () => void
  /** carroceria opaca com amassamentos e dano estrutural em raio-X */
  lataSolida?: boolean
  aoAlternarLataSolida?: (ativa: boolean) => void
  modoInvestigadorAtivo?: boolean
  aoAlternarModoInvestigador?: () => void
  /** Ocupante em foco — agora também escolhido tocando no corpo na cena. */
  ocupanteEmFoco: {
    id: string
    nome: string
    tipo: string
    posicao: string
    retencao: string
    reacao?: string
  } | null
  /**
   * Só existe quando o professor chegou aqui por um pulo de outro módulo
   * (ex.: "Ver cinemática desta vítima" da Oficina) — nunca por um clique
   * direto na aba Cinemática. Mostra o botão "Voltar".
   */
  rotuloOrigem?: string
  aoVoltar?: () => void
}

/**
 * Cinemática: evento, ocupantes, sobreposições, reprodução e resultados.
 */
export function ferramentasCinematica(p: PropsCinematica): FerramentasModulo {
  return {
    grupos: [
      { id: 'evento', rotulo: 'Evento', icone: <IconeEvento />, conteudo: p.painelEvento },
      {
        id: 'ocupantes',
        rotulo: 'Ocupantes',
        icone: <IconeOcupantes />,
        badge: p.quantidadeOcupantes,
        conteudo: p.painelOcupantes,
      },
      {
        id: 'sobreposicoes',
        rotulo: 'Sobreposições',
        icone: <IconeCamadas />,
        conteudo: p.painelSobreposicoes,
      },
      {
        id: 'reproducao',
        rotulo: 'Reprodução',
        icone: <IconeReproduzir />,
        conteudo: p.painelReproducao,
      },
      {
        id: 'resultados',
        rotulo: 'Lesões e Presos',
        icone: <IconeResultados />,
        conteudo: p.painelResultados,
      },
    ],
    chips: [],
    inspetor: p.ocupanteEmFoco && {
      titulo: p.ocupanteEmFoco.nome,
      subtitulo: p.ocupanteEmFoco.tipo,
      propriedades: [
        { rotulo: 'Posição', valor: p.ocupanteEmFoco.posicao },
        { rotulo: 'Retenção', valor: p.ocupanteEmFoco.retencao },
        ...(p.ocupanteEmFoco.reacao ? [{ rotulo: 'Reação no evento', valor: p.ocupanteEmFoco.reacao }] : []),
        { rotulo: 'Fase', valor: p.fase },
      ],
    },
    acoesRapidas: (
      <>
        {p.aoVoltar && (
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--secundario"
            onClick={p.aoVoltar}
            title={p.rotuloOrigem ? `Voltar para ${p.rotuloOrigem}` : 'Voltar'}
          >
            <IconeVoltarModulo />Voltar{p.rotuloOrigem ? ` para ${p.rotuloOrigem}` : ''}
          </button>
        )}
        {p.aoAbrirQuatroImpactos && (
          <button type="button" className="btn-acao-cad btn-acao-cad--destaque" onClick={p.aoAbrirQuatroImpactos} title="Mostrar os quatro impactos na cena principal">
            <IconeReproduzir />4 impactos
          </button>
        )}
        <button
          type="button"
          className="btn-acao-cad btn-acao-cad--primario"
          onClick={p.aoAlternarReproducao}
          title={p.reproduzindo ? 'Pausar simulação' : 'Iniciar simulação da colisão'}
        >
          {p.reproduzindo ? <><IconePausar />Pausar</> : <><IconeReproduzir />Simular</>}
        </button>
        <button
          type="button"
          className="btn-acao-cad btn-acao-cad--secundario"
          onClick={p.aoReiniciar}
          title="Reiniciar simulação"
        >
          <IconeReiniciar />Reiniciar
        </button>
        {p.aoAlternarLataSolida && (
          <button
            type="button"
            className={`btn-acao-cad ${p.lataSolida ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
            onClick={() => p.aoAlternarLataSolida?.(!p.lataSolida)}
            aria-pressed={Boolean(p.lataSolida)}
            title="Lataria sólida: mostra os amassamentos na chapa e o dano estrutural em raio-X"
          >
            <IconeCamadas />{p.lataSolida ? 'Lataria: sólida' : 'Lataria: translúcida'}
          </button>
        )}
      </>
    ),
  }
}
